import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

interface CsvAccountInfoModalProps {
  open: boolean;
  onClose: () => void;
  accountList: {
    email: string;
    account_name: string;
    temp_password: string;
  }[];
  errorHeaderLocation: string;
  onDownloadCsv: () => void;
}

export function CsvAccountInfoModal({
  open,
  onClose,
  accountList,
  errorHeaderLocation,
  onDownloadCsv,
}: CsvAccountInfoModalProps) {
  const [showPassword, setShowPassword] = useState(false);

  const handleClose = () => {
    setShowPassword(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick") return;
        handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>登録アカウント情報一覧</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          以下のアカウントが登録されました。初期パスワードを安全な場所に保存してください。
        </Typography>
        <TableContainer component={Paper} sx={{ maxHeight: "60vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>アカウント名</TableCell>
                <TableCell>メールアドレス</TableCell>
                <TableCell>
                  初期パスワード
                  <Tooltip title={showPassword ? "パスワードを隠す" : "パスワードを表示"}>
                    <IconButton onClick={() => setShowPassword((prev) => !prev)} size="small" sx={{ ml: 0.5 }}>
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">コピー</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accountList.map((acc, i) => (
                <TableRow key={"csvAccount" + i}>
                  <TableCell>{acc.account_name}</TableCell>
                  <TableCell>{acc.email}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {acc.temp_password
                      ? (showPassword ? acc.temp_password : "●●●●●●●●")
                      : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="アカウント情報をコピー">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const text = `アカウント名: ${acc.account_name}\nメールアドレス: ${acc.email}\n初期パスワード: ${acc.temp_password || "なし"}`;
                          navigator.clipboard.writeText(text);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          fullWidth
          sx={{ mt: 1 }}
          onClick={() => {
            const text = accountList.map((acc) =>
              `アカウント名: ${acc.account_name}\nメールアドレス: ${acc.email}\n初期パスワード: ${acc.temp_password || "なし"}`
            ).join("\n---\n");
            navigator.clipboard.writeText(text);
          }}
        >
          すべてコピー
        </Button>
      </DialogContent>
      <DialogActions>
        {errorHeaderLocation !== "None" && (
          <Button
            onClick={() => {
              const link = document.createElement("a");
              link.href = errorHeaderLocation;
              document.body.appendChild(link);
              link.click();
              link.parentNode?.removeChild(link);
            }}
            variant="outlined"
            color="error"
          >
            エラー内容ダウンロード
          </Button>
        )}
        <Button onClick={onDownloadCsv} variant="outlined">
          CSVダウンロード
        </Button>
        <Button onClick={handleClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
}
